import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, X, Download } from 'lucide-react';
import ElementCard from './ElementCard';
import ELEMENTS, { getElementsByBlock } from '../data/elements';
import BLOCKS, { BLOCK_ORDER } from '../data/blocks';

export default function InteractiveTable({ onElementClick }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBlock, setSelectedBlock] = useState('all');
  const [showImage, setShowImage] = useState(true);

  const filteredElements = useMemo(() => {
    let filtered = ELEMENTS;
    
    if (selectedBlock !== 'all') {
      filtered = filtered.filter(el => el.block === selectedBlock);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(el =>
        el.symbol.toLowerCase().includes(query) ||
        el.name.toLowerCase().includes(query) ||
        el.description.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [searchQuery, selectedBlock]);

  const elementsByBlock = useMemo(() => {
    const grouped = {};
    BLOCK_ORDER.forEach(blockId => {
      grouped[blockId] = filteredElements.filter(el => el.block === blockId);
    });
    return grouped;
  }, [filteredElements]);

  return (
    <div className="max-w-7xl mx-auto px-4">
      {/* Section Header */}
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
          The Complete <span className="text-gradient">Periodic Table</span>
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Explore 168 elements of AI organized into 11 functional blocks. 
          Click any element to learn more.
        </p>
      </motion.div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search elements..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-dark-800 border border-white/10 rounded-xl focus:border-cyber-blue focus:ring-1 focus:ring-cyber-blue outline-none transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Block Filter */}
        <div className="relative">
          <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none" />
          <select
            value={selectedBlock}
            onChange={(e) => setSelectedBlock(e.target.value)}
            className="pl-12 pr-8 py-3 bg-dark-800 border border-white/10 rounded-xl appearance-none cursor-pointer min-w-[200px]"
          >
            <option value="all">All Blocks</option>
            {BLOCK_ORDER.map(blockId => (
              <option key={blockId} value={blockId}>
                {BLOCKS[blockId].code} - {BLOCKS[blockId].name}
              </option>
            ))}
          </select>
        </div>

        {/* View Toggle */}
        <button
          onClick={() => setShowImage(!showImage)}
          className="px-4 py-3 bg-dark-800 border border-white/10 rounded-xl hover:bg-dark-700 transition-colors flex items-center gap-2"
        >
          <Download className="w-5 h-5" />
          <span className="hidden md:inline">{showImage ? 'Hide' : 'Show'} Full Image</span>
        </button>
      </div>

      {/* Results Count */}
      <div className="mb-6 text-sm text-gray-500">
        Showing {filteredElements.length} of {ELEMENTS.length} elements
      </div>

      {/* Full Image View */}
      {showImage && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mb-12"
        >
          <img
            src="/images/Full_AI_Periodic_Table_Poster.png"
            alt="The Complete AI Periodic Table"
            className="w-full rounded-xl border border-white/10"
          />
          <p className="text-center text-sm text-gray-500 mt-4">
            Full AI Periodic Table - Right click to save image
          </p>
        </motion.div>
      )}

      {/* Interactive Table */}
      <div className="space-y-8">
        {BLOCK_ORDER.map(blockId => {
          const block = BLOCKS[blockId];
          const elements = elementsByBlock[blockId];
          
          if (elements.length === 0) return null;
          
          return (
            <motion.div
              key={blockId}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              className="bg-dark-900/50 rounded-2xl p-6 border border-white/5"
            >
              {/* Block Header */}
              <div className="flex items-center gap-4 mb-6">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                  style={{ backgroundColor: `${block.color}20` }}
                >
                  {block.icon}
                </div>
                <div>
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <span 
                      className="text-sm font-mono px-2 py-0.5 rounded"
                      style={{ backgroundColor: block.color, color: '#000' }}
                    >
                      {block.code}
                    </span>
                    <span style={{ color: block.color }}>{block.name}</span>
                    {block.isSpeculative && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-500">
                        SPECULATIVE
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-gray-500">{block.description}</p>
                </div>
                <div className="ml-auto text-right">
                  <span className="text-2xl font-bold" style={{ color: block.color }}>
                    {elements.length}
                  </span>
                  <span className="text-gray-500 text-sm ml-1">elements</span>
                </div>
              </div>

              {/* Elements Grid */}
              <div className="flex flex-wrap gap-2">
                {elements.map((element) => (
                  <ElementCard
                    key={element.symbol}
                    element={element}
                    onClick={onElementClick}
                    size="default"
                  />
                ))}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Legend */}
      <motion.div
        className="mt-12 p-6 bg-dark-900/50 rounded-2xl border border-white/5"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h3 className="text-lg font-bold mb-4">Block Legend</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {BLOCK_ORDER.map(blockId => {
            const block = BLOCKS[blockId];
            return (
              <button
                key={blockId}
                onClick={() => setSelectedBlock(selectedBlock === blockId ? 'all' : blockId)}
                className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                  selectedBlock === blockId ? 'bg-white/10' : 'hover:bg-white/5'
                }`}
              >
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: block.color }}
                />
                <span className="text-sm">
                  <span className="font-mono text-gray-500">{block.code}</span>
                  <span className="text-white ml-1">{block.name}</span>
                </span>
              </button>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}
